import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classnames from 'classnames';
import moment from 'moment-timezone';
import { get, last, some } from 'lodash';
import { getUserTypeLabel } from '../../../auth/constants';
import { t } from '../../../../modules/infra/i18n';

import AutoChargeRecurring from './AutoChargeRecurring';
import AutoHeight from '../../../common/AutoHeight';
import CustomerNotes from './notes/CustomerNotes';
import DateTimePicker from './dateTimePicker/dateTimePicker';
import DoNotChargeTax from './DoNotChargeTax';
import FormErrorMessage from '../../../common/ScrollingFormErrorMessage';
import Header from './header';
import Notes from './notes/Notes';
import PreferredTip from './PreferredTip';
import RepeatAppointment from './RepeatAppointment';
import SaveButton from '../../../common/buttons/saveButton';
import SelectBarber from './selectBarber';
import SelectCustomer from './selectCustomer';
import SelectDuration from './selectDuration';
import SelectServices from './selectServices';
import SendNotifications from './confirmationScreen/sendNotifications';
import DateTime from '../../../common/DateTime';
import { canUserModifyAppointments, canUserViewAppointmentContactInfo } from '../../../common/helpers/users';

class AppointmentForm extends Component {
  static propTypes = {
    appointment: PropTypes.object,
    handlers: PropTypes.object,
    isRecurring: PropTypes.bool.isRequired,
    recurrUntil: PropTypes.string,
    recurrEveryNumDays: PropTypes.number.isRequired,
    selectedBarber: PropTypes.string,
    currentBarber: PropTypes.object,
    selectedServices: PropTypes.arrayOf(PropTypes.object),
    selectedCustomer: PropTypes.object,
    selectedLeadId: PropTypes.string.isRequired,
    selectedDuration: PropTypes.number,
    day: PropTypes.string,
    time: PropTypes.string,
    dateTime: PropTypes.string,
    appointmentNotes: PropTypes.string.isRequired,
    allowMultipleServices: PropTypes.bool.isRequired,
    customerError: PropTypes.bool.isRequired,
    barberError: PropTypes.bool.isRequired,
    serviceError: PropTypes.bool.isRequired,
    dateError: PropTypes.bool.isRequired,
    timeError: PropTypes.bool.isRequired,
    notesError: PropTypes.bool.isRequired,
    saveButtonDisabled: PropTypes.bool.isRequired,
    isPaid: PropTypes.bool.isRequired,
    sendNotifications: PropTypes.bool.isRequired,
    confirmationScreenShowed: PropTypes.bool.isRequired,
    doNotChargeTax: PropTypes.bool.isRequired,
    autoChargeRecurring: PropTypes.bool.isRequired,
    isShopUserOrRentalBarberUser: PropTypes.bool.isRequired,
    tipAmount: PropTypes.number.isRequired,
    windowHeight: PropTypes.number.isRequired,
    bookedWithAnyBarber: PropTypes.bool.isRequired,
  }

  static defaultProps = {
    appointment: {},
    handlers: {},
    recurrUntil: null,
    selectedBarber: '',
    selectedServices: [],
    selectedCustomer: {},
    selectedDuration: undefined,
    day: null,
    time: null,
    dateTime: null,
    currentBarber: {},
  }

  getRescheduledByKind() {
    const rescheduledBy = get(this.props.appointment, 'rescheduledBy');
    const lastRescheduledBy = last(rescheduledBy);
    const rescheduledByKind = get(lastRescheduledBy, 'rescheduleByKind');
    return rescheduledByKind;
  }

  getRescheduledByTime() {
    const rescheduledBy = get(this.props.appointment, 'rescheduledBy');
    const lastRescheduledTime = last(rescheduledBy);
    const rescheduledByTime = get(lastRescheduledTime, 'rescheduleTime');
    return rescheduledByTime;
  }

  getCanceledByKind() {
    return get(this.props.appointment, 'canceledBy.cancelByKind');
  }

  getCanceledByTime() {
    return get(this.props.appointment, 'canceledBy.cancelTime');
  }

  hasTaxes() {
    return some(this.props.selectedServices, 'tax');
  }

  showModified() {
    return (this.props.appointment.createdAt !== this.props.appointment.updatedAt);
  }

  showCanceled() {
    return this.props.appointment.canceledBy;
  }

  renderActionControls() {
    const { saveButtonDisabled } = this.props;
    return (<SaveButton content={t('appointment:form.save_button')} disabled={saveButtonDisabled} />);
  }

  render() {
    const {
            selectedBarber,
      currentBarber,
      selectedServices,
      selectedCustomer,
      selectedLeadId,
      selectedDuration,
      day,
      time,
      dateTime,
      appointmentNotes,
      allowMultipleServices,
      customerError,
      barberError,
      serviceError,
      dateError,
      timeError,
      notesError,
      appointment,
      handlers,
      isRecurring,
      recurrUntil,
      recurrEveryNumDays,
      isPaid,
      confirmationScreenShowed,
      sendNotifications,
      doNotChargeTax,
      autoChargeRecurring,
      isShopUserOrRentalBarberUser,
      tipAmount,
  } = this.props;

    let appointmentStart;
    let appointmentId;
    if (appointment) {
      appointmentStart = moment.utc(appointment.start).toJSON();
      appointmentId = appointment.id;
    }

    const formClassName = classnames('c-appointment-form',
            { 'has-confirmation-opened': confirmationScreenShowed }
        );
    const canModifyAppointments = this.edit
      ? (canUserModifyAppointments(this.props.permissions) && !this.showCanceled())
      : true;
    const canViewContactInfo = canUserViewAppointmentContactInfo(this.props.permissions);

    return (
      <AutoHeight>
        <form
          className={formClassName}
          onSubmit={handlers.update()}
          data-automation-id="appointment-form"
          style={{ maxHeight: this.props.windowHeight }}
        >
          <Header onClose={this.props.onClose} />
          <div className="u-ph u-pt-">
            <div className="layout o-flexbox">
              <div className="u-1/2-medium-and-up">
                <SelectBarber
                  selectedBarber={selectedBarber || -1}
                  selectBarber={handlers.selectBarber}
                  bookedWithAnyBarber={this.props.bookedWithAnyBarber}
                  onAnyBarberChange={handlers.onAnyBarberChange}
                  canModifyAppointments={canModifyAppointments}
                />
                <FormErrorMessage
                  display-if={barberError}
                  text={t('appointment:form.select_barber_error')}
                />
                <SelectServices
                  appointment={appointment}
                  selectedBarber={selectedBarber}
                  selectedServices={selectedServices}
                  selectServices={handlers.selectServices}
                  allowMultipleServices={allowMultipleServices}
                  canModifyAppointments={canModifyAppointments}
                />
                <FormErrorMessage
                  display-if={serviceError}
                  text={t('appointment:form.select_service_error')}
                />
                <div className="layout__item u-pl0 u-mt-- u-mb0 u-mb--">
                  <SelectDuration
                    selectedBarber={selectedBarber}
                    selectedServices={selectedServices}
                    selectedDuration={selectedDuration}
                    selectDuration={handlers.selectDuration}
                    canModifyAppointments={canModifyAppointments}
                  />
                </div>
                <div className="layout__item u-1/2 u-pl0 u-mt-- u-mb0 u-mb--">
                  <RepeatAppointment
                    recurrEveryNumDays={recurrEveryNumDays}
                    recurrUntil={recurrUntil}
                    onFrequencyChange={handlers.onRecurringFrequencyChange}
                    onDateChange={handlers.onRecurringEndDateChange}
                    active={isRecurring}
                    disabled={this.edit}
                  />
                </div>
              </div>
              <div className="u-1/2-medium-and-up">
                <SelectCustomer
                  selectCustomer={handlers.selectCustomer}
                  selectedCustomer={selectedCustomer}
                  setCustomerDetails={handlers.setCustomerDetails}
                  selectStatus={this.renderSelectStatus && this.renderSelectStatus()}
                  selectedLeadId={selectedLeadId}
                  selectLeadId={handlers.selectLeadId}
                  isPaid={isPaid}
                  canModifyAppointments={canModifyAppointments}
                  canViewContactInfo={canViewContactInfo}
                  customerAction={this.renderCustomerAction && this.renderCustomerAction()}
                />
                <FormErrorMessage
                  display-if={customerError}
                  text={t('appointment:form.customer_name_error')}
                />
                <CustomerNotes disabled={!canModifyAppointments} />
                <Notes
                  value={appointmentNotes}
                  onChange={handlers.onChangeAppointmentNotes}
                  disabled={!canModifyAppointments}
                />
                <FormErrorMessage
                  display-if={notesError}
                  text={t('appointment:form.notes_minimum_error')}
                />
                <div
                  display-if={isShopUserOrRentalBarberUser && isRecurring}
                  className="layout__item"
                >
                  <AutoChargeRecurring
                    checked={autoChargeRecurring}
                    disabled={!canModifyAppointments}
                    onChange={handlers.toggleAutoChargeRecurring}
                    customer={selectedCustomer}
                  />
                  <PreferredTip
                    disabled={!canModifyAppointments}
                    value={tipAmount}
                    onChange={handlers.setTipAmount}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="o-flexbox o-flexbox--justify-end u-ph">
            {this.renderCharge && this.renderCharge()}
            {this.renderAddProducts && this.renderAddProducts()}
            {this.renderAddTip && this.renderAddTip()}
          </div>
          <div className="u-ph u-pb-">
            <DateTimePicker
              selectedBarber={selectedBarber}
              currentBarber={currentBarber}
              setDate={handlers.setDate}
              day={day}
              time={time}
              dateTime={dateTime}
              appointmentStart={appointmentStart}
              appointmentId={appointmentId}
              selectedServices={selectedServices}
              selectedDuration={selectedDuration}
              edit={this.edit}
              canModifyAppointments={canModifyAppointments}
            />
            <FormErrorMessage
              display-if={dateError || timeError}
              text={t('appointment:form.select_time_error')}
            />
          </div>
          {this.renderPaymentInfo && this.renderPaymentInfo()}
          <div className="u-ph u-pb" display-if={!this.edit}>
            <div className="layout u-mt-">
              <SendNotifications
                checked={sendNotifications}
                onChange={handlers.toggleNotifications}
              />
            </div>
          </div>
          <div className="u-ph u-pb" display-if={this.hasTaxes()}>
            <div className="layout">
              <DoNotChargeTax
                checked={doNotChargeTax}
                onChange={handlers.toggleDoNotChargeTax}
                disabled={!canModifyAppointments}
              />
            </div>
          </div>
          <div className="u-mt+ u-mb u-mr u-text-right" display-if={appointment}>
            <div>
              <div className="c-label u-type--normal u-inline-block u-mr--">{t('appointment:details.created_label')}</div>
              <div className="c-label u-type--normal u-inline-block">
                {getUserTypeLabel(get(appointment, 'bookedBy.kind'))}
                <span className="u-ph--">&ndash;</span>
              </div>
              <div className="c-label u-type--normal u-inline-block">
                <DateTime>{appointment.createdAt}</DateTime>
              </div>
            </div>
            <div display-if={this.showModified() && getUserTypeLabel(this.getRescheduledByKind())}>
              <div className="c-label u-type--normal u-inline-block u-mr--">{t('appointment:details.modified_label')}</div>
              <div className="u-inline-block">
                <div className="c-label u-type--normal u-inline-block">
                  {getUserTypeLabel(this.getRescheduledByKind())}
                  <span className="u-ph--">&ndash;</span>
                </div>
              </div>
              <div className="c-label u-inline-block u-type--normal">
                <DateTime>{this.getRescheduledByTime()}</DateTime>
              </div>
            </div>
            <div display-if={this.showCanceled() && getUserTypeLabel(this.getCanceledByKind())}>
              <div className="c-label u-type--normal u-inline-block u-mr--">{t('appointment:details.canceled_label')}</div>
              <div className="u-inline-block">
                <div className="c-label u-type--normal u-inline-block">
                  {getUserTypeLabel(this.getCanceledByKind())}
                  <span className="u-ph--">&ndash;</span>
                </div>
              </div>
              <div className="c-label u-inline-block u-type--normal">
                <DateTime>{this.getCanceledByTime()}</DateTime>
              </div>
            </div>
          </div>
          <div className="c-appointment-form__controls-spacer" />
          <div className="c-appointment-form__controls">
            <div className="c-separator u-mb-" />
            <div className="u-text-right u-ph- u-pb-">
              <div onClick={this.props.onClose} className="c-btn c-btn--grey u-width-120 u-float-left">
                <span className="c-btn__text">{t('appointment:form.close_button')}</span>
              </div>
              <div className="u-float-left u-ml--">
                {this.renderWaiver && this.renderWaiver()}
              </div>
              {this.renderActionControls && this.renderActionControls()}
            </div>
          </div>
          {this.renderConfirmationScreen && this.renderConfirmationScreen()}
        </form>
      </AutoHeight>
    );
  }
}

export default AppointmentForm;
